import { cx } from '@emotion/css';
import { Icon, MessageContext } from '@snapchat/snap-design-system-marketing';
import type { FC } from 'react';
import { useContext } from 'react';

import { ShopifyContext } from '../ShopifyProvider';
import { Price } from './Price';
import {
  atpCss,
  cartItemImg,
  cartItemWrapperCss,
  depositAmountCss,
  depositInfoCss,
  depositLabelCss,
  fullPriceStrikethroughCss,
  priceCss,
  quantityCss,
  quantityModificationCss,
  quantityModificationDisabledCss,
  quantityWrapperCss,
  titleCss,
} from './styles';
import type { ShopifyProduct } from './types';

export const CartItem: FC<ShopifyProduct> = ({
  image,
  atpMessage,
  price,
  handle,
  id,
  maxQuantity,
  productMaxQuantity,
  variantTitle,
  sellingPlanData,
}) => {
  const { cart, products, currencyCode = '' } = useContext(ShopifyContext);
  const { formatMessage } = useContext(MessageContext);

  const contentfulProduct = products?.contentful[handle] ?? {};

  // Cart is keyed by variant ID
  const cartKey = id;
  const quantity = cart.lineItems[cartKey] ?? 0;
  // productMaxQuantity: from product-level metafield (custom.maxquantity) — preferred source.
  // maxQuantity: from variant-level metafield (global.maxQuantity) — deprecated, kept for backward compatability
  const effectiveMaxQuantity = productMaxQuantity ?? maxQuantity;
  const buttonDisabled = effectiveMaxQuantity === quantity;

  // Deposit amount from product metafield, looked up by current currency.
  const depositAmount = sellingPlanData && currencyCode ? sellingPlanData[currencyCode] : null;

  return (
    <div className={cartItemWrapperCss}>
      <div className={cartItemImg(image)} />
      <div className={titleCss}>
        {contentfulProduct.title}
        {variantTitle && variantTitle !== 'Default Title' ? ` - ${variantTitle}` : ''}
      </div>
      {depositAmount ? (
        <div className={depositInfoCss}>
          <Price className={fullPriceStrikethroughCss} productPrice={price} />
          <Price className={depositAmountCss} productPrice={depositAmount} />
          <div className={depositLabelCss}>
            {formatMessage({ id: 'depositDueToday', defaultMessage: 'Deposit due today' })}
          </div>
        </div>
      ) : (
        <Price className={priceCss} productPrice={price} />
      )}
      <div className={quantityWrapperCss}>
        <button
          onClick={() =>
            cart.removeOne({
              handle,
              variantId: cartKey,
              contentfulProduct,
              shopifyProducts: products.shopify,
              currencyCode,
            })
          }
          className={quantityModificationCss}
        >
          <Icon name="remove" />
        </button>
        <span className={quantityCss}>{quantity}</span>
        <button
          disabled={buttonDisabled}
          onClick={() =>
            cart.addOne({
              handle,
              variantId: cartKey,
              contentfulProduct,
              shopifyProducts: products.shopify,
              currencyCode,
            })
          }
          className={cx(quantityModificationCss, {
            [quantityModificationDisabledCss]: buttonDisabled,
          })}
        >
          <Icon name="add" />
        </button>
      </div>
      {atpMessage && <div className={atpCss}>{atpMessage}</div>}
    </div>
  );
};
